import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import styles from './PinInput.module.css';

function sanitizeChar(raw, alphanumeric) {
  if (alphanumeric) {
    return raw.replace(/[^a-zA-Z0-9]/g, '').slice(-1).toUpperCase();
  }
  return raw.replace(/\D/g, '').slice(-1);
}

function sanitizePaste(raw, alphanumeric, length) {
  const cleaned = alphanumeric
    ? raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    : raw.replace(/\D/g, '');
  return cleaned.slice(0, length);
}

/**
 * Casillas tipo OTP para PIN o código. `length` define cuántas casillas se
 * muestran (PIN de 4 o 6 dígitos). Vía ref expone `focus()` (primera casilla).
 */
const PinInput = forwardRef(function PinInput({
  length = 4,
  value = [],
  onChange,
  disabled = false,
  className = '',
  /** Oculta dígitos como en la app (•). */
  masked = true,
  /** Botón ojo para mostrar/ocultar. */
  showToggle = true,
  /** `numeric` (PIN) o `alphanumeric` (código de reset). */
  mode = 'numeric',
  /** Nombre accesible del grupo de casillas (ej. "Nuevo PIN"). */
  ariaLabel,
}, ref) {
  const refs = useRef([]);

  useImperativeHandle(ref, () => ({
    focus: () => refs.current[0]?.focus(),
  }));
  const [visible, setVisible] = useState(!masked);
  const obscured = masked && !visible;
  const alphanumeric = mode === 'alphanumeric';

  const handleChange = useCallback(
    (index, digit) => {
      const d = sanitizeChar(digit, alphanumeric);
      const next = [...value];
      while (next.length < length) next.push('');
      next[index] = d;
      onChange(next.slice(0, length));
      if (d && index < length - 1) {
        refs.current[index + 1]?.focus();
      }
    },
    [value, length, onChange, alphanumeric]
  );

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = sanitizePaste(
      e.clipboardData.getData('text'),
      alphanumeric,
      length
    );
    const next = pasted.split('');
    while (next.length < length) next.push('');
    onChange(next.slice(0, length));
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className={`${styles.wrap} ${className}`}>
      {showToggle && masked && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Ocultar PIN' : 'Mostrar PIN'}
          aria-pressed={visible}
        >
          <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />
        </button>
      )}
      <div
        className={styles.row}
        onPaste={handlePaste}
        role="group"
        aria-label={ariaLabel}
      >
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type={obscured ? 'password' : 'text'}
            inputMode={alphanumeric ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoCapitalize={alphanumeric ? 'characters' : 'off'}
            maxLength={1}
            className={`${styles.cell} ${alphanumeric ? styles.cellCode : ''}`}
            value={value[i] || ''}
            disabled={disabled}
            aria-label={`${alphanumeric ? 'Carácter' : 'Dígito'} ${i + 1}`}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>
    </div>
  );
});

export default PinInput;
